import { Button, Input, Select, Space, TableProps, Tooltip } from "antd";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import StatusItem from "../../../../components/common/StatusItem";
import CustomTable from "../../../../components/ui/CustomTable";
import { API_URL } from "../../../../constants/apiUrl";
import { AUTH_MANAGE_API } from "../../../../constants/constants";
import {
  AllKpiPerformanceParams,
  AllKpiPerformanceResponse,
} from "../../../../features/organization/contractual/JobDetails/jobDetails.type";
import { useGetAllKPIPerformanceHistoryQuery } from "../../../../features/organization/contractual/JobDetails/jobDetailsApi";
import useDownloadEmployeeXlsx from "../../../../hooks/useDownloadExcelWithApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import { formatDate } from "../../../../lib/time";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../../utils/utils";
import ExportIcon from "../_icons/ExportIcon";
import ManageAssignKpi from "./ManageAssignKpi";

export const STATUSES = [
  {
    label: "Upcoming",
    value: "upcoming",
    className: "status-link rounded-lg",
  },
  {
    label: "Ongoing",
    value: "ongoing",
    className: "status-warning rounded-lg",
  },
  {
    label: "Completed",
    value: "completed",
    className: "status-success rounded-lg",
  },
];

const KPIPerformance = () => {
  const { open, handleOpen, handleClose } = useModal();
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  const { id, memberId } = useParams();
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useTable<AllKpiPerformanceParams>();

  const { data, isLoading, isError, error } =
    useGetAllKPIPerformanceHistoryQuery({
      arg: {
        params: {
          ...filter,
          search: keyword,
        },
      },
      jobApplyId: Number(id),
      memberId: Number(memberId),
    });

  useError(isError, error, "Failed to fetch KPI performance data");

  const columns: TableProps<AllKpiPerformanceResponse>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "KPI Group",
      dataIndex: "kpi_group",
      render: (val) => valueOrNa(val?.group_name),
    },

    {
      align: "center",
      title: "Date Period",
      render: (record: any) =>
        `${
          record?.effective_from
            ? formatDate(record?.effective_from, "DD MMM, YYYY")
            : ""
        } – ${record?.effective_to ? formatDate(record?.effective_to) : ""}`,
    },
    {
      align: "center",
      title: "Effective Date",
      dataIndex: "effective_from",
      render: (val) => formatDate(val),
    },

    {
      align: "center",
      title: "Number of KPIs",
      dataIndex: "kpi_count",
      render: (val) => addCommaInNumber(val || 0),
    },

    {
      align: "center",
      title: "Total Achievement",
      dataIndex: "total_batch_achievement_score",
      render: (val) => <span>{`${val || 0}%`}</span>,
    },
    {
      align: "center",
      title: "Status",
      dataIndex: "computed_status",
      render: (val) => <StatusItem value={val} list={STATUSES} />,
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (record) => (
        <Space align="center">
          <Link
            to={`/organization/my-projects/details/${id}/${memberId}/${
              record?.kpi_group_id
            }?date=${formatDate(
              record?.effective_from,
              "DD MMM, YYYY"
            )} - ${formatDate(record?.effective_to)}`}
          >
            <Tooltip title="View">
              <Button
                className="action-view"
                size="small"
                icon={<ViewIcon />}
              />
            </Tooltip>
          </Link>
        </Space>
      ),
    },
  ];

  // ---------- Excel Download Function   ------------
  const handleDownload = () => {
    downloadXlsx(
      `${
        AUTH_MANAGE_API + API_URL.getAllKpiPerformanceHistory
      }/${id}/member/${memberId}`,
      {
        ...filter,
        page: page,
        search: keyword,
        skip: 0,
        limit: 1000000000000000,
      },
      `KPI_Performance_${moment().format("DD MMMM YYYY")}_${moment().format(
        "h.mm.ss A"
      )}`,
      "assignments",
      (item: AllKpiPerformanceResponse["assignments"][0]) => {
        return {
          "KPI Group": item?.kpi_group?.group_name || "-",
          "Date Period":
            item?.effective_from || item?.effective_to
              ? `${
                  item?.effective_from
                    ? formatDate(item?.effective_from, "DD MMM, YYYY")
                    : ""
                } – ${
                  item?.effective_to
                    ? formatDate(item?.effective_to, "DD MMM, YYYY")
                    : ""
                }`
              : "-",

          "Effective Date": formatDate(item?.effective_from) || "-",
          "Number of KPIs":
            item?.kpi_count !== undefined && item?.kpi_count !== null
              ? addCommaInNumber(item?.kpi_count)
              : "-",
          "Total Achievement":
            item?.total_achievement_percentage !== undefined &&
            item?.total_achievement_percentage !== null
              ? addCommaInNumber(item?.total_achievement_percentage) + "%"
              : "-",
          Status: capitalize(item?.computed_status) || "-",
        };
      }
    );
  };

  return (
    <>
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data.total}
        dataSource={addKeyInArray(data?.data?.assignments)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              filterOption={selectSearchOption}
              options={selectArrayOption(STATUSES, "label", "value")}
              onChange={(value) => handleFilter("status", value)}
            />

            {/* <Button
              type="default"
              className="!bg-cyan-50/50 !border-cyan-500 !text-cyan-500"
            >
              <ImportIcon /> Import
            </Button> */}

            <Button
              type="default"
              className="!bg-purple-50 !border-fuchsia-600 !text-fuchsia-600"
              loading={excelLoading}
              onClick={handleDownload}
            >
              <ExportIcon /> Export
            </Button>

            <Button
              type="primary"
              color="primary"
              onClick={() => handleOpen({ type: "ADD" })}
            >
              Assign Another KPI
            </Button>
          </>
        }
      />

      <ManageAssignKpi
        type={open.type}
        handleClose={handleClose}
        open={open.open && open.type === "ADD"}
      />
    </>
  );
};

export default KPIPerformance;
